import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { FontAwesome } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import ErrorBoundary from '../../components/common/error-boundary';
import useTheme from '../../hooks/useTheme';

interface StackErrorBoundaryProps {
  children: React.ReactNode;
  stackName: string;
}

function StackErrorFallback({ resetError, stackName }: { resetError: () => void; stackName: string }) {
  const { colors } = useTheme();
  const { t } = useTranslation();

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <FontAwesome name="exclamation-triangle" size={48} color={colors.warning} />
      <Text style={[styles.title, { color: colors.text }]}>
        {t('common.error')}
      </Text>
      <Text style={[styles.message, { color: colors.textSecondary }]}>
        {t('navigation.stackError', { stack: stackName })}
      </Text>
      <TouchableOpacity 
        style={[styles.button, { backgroundColor: colors.primary }]}
        onPress={resetError}
        accessibilityLabel={t('common.retry')}
        accessibilityRole="button"
      >
        <Text style={styles.buttonText}>{t('common.retry')}</Text>
      </TouchableOpacity>
    </View>
  );
}

export function StackErrorBoundary({ children, stackName }: StackErrorBoundaryProps) {
  return (
    <ErrorBoundary
      level="stack"
      fallback={(resetError) => <StackErrorFallback resetError={resetError} stackName={stackName} />}
    >
      {children}
    </ErrorBoundary>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    marginTop: 16,
    marginBottom: 8,
  },
  message: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 24,
  },
  button: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
});